/**
 * Booking Confirmation Templates Modal
 * Displays booking confirmation message templates (WhatsApp and Email) after booking creation
 * Allows manual confirmation tracking
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Tabs,
  Button,
  Space,
  message,
  Card,
  Typography,
  Checkbox,
  Divider,
  Tag,
  Spin,
  Alert,
  Input,
} from "antd";
import { Box } from "@mui/material";
import {
  CheckCircleOutlined,
  CopyOutlined,
  CheckOutlined,
  MessageOutlined,
  MailOutlined,
  CalendarOutlined,
  TeamOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { getConfirmationTemplates, updateConfirmation } from "../../quotes/services/bookingService";
import { copyToClipboard } from "../../leads/services/communicationTemplateService";
import api from "@/shared/api";
import dayjs from "dayjs";

const { Text, Title } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const BookingConfirmationTemplatesModal = ({
  visible,
  orderId,
  booking,
  onClose,
  onConfirmationUpdate,
  userId,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [whatsappTemplate, setWhatsappTemplate] = useState("");
  const [emailTemplate, setEmailTemplate] = useState({ subject: "", body: "" });
  const [editableEmailTemplate, setEditableEmailTemplate] = useState({ subject: "", body: "" });
  const [whatsappCopied, setWhatsappCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [confirmations, setConfirmations] = useState({
    whatsappSent: false,
    emailSent: false,
    reminderSent: false,
  });
  const [updatingConfirmations, setUpdatingConfirmations] = useState(false);

  // Load templates when modal opens
  useEffect(() => {
    if (visible && orderId) {
      loadTemplates();
      loadConfirmations();
    }
  }, [visible, orderId]);

  // Load confirmation status from booking prop
  useEffect(() => {
    if (booking?.confirmations) {
      setConfirmations({
        whatsappSent: booking.confirmations.whatsappSent || false,
        emailSent: booking.confirmations.emailSent || false,
        reminderSent: booking.confirmations.reminderSent || false,
      });
    }
  }, [booking]);

  // Load templates from API
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const result = await getConfirmationTemplates(orderId);
      if (result.success && result.data) {
        setWhatsappTemplate(result.data.whatsapp || "");
        const emailData = result.data.email || { subject: "", body: "" };
        setEmailTemplate(emailData);
        setEditableEmailTemplate(emailData);
      } else {
        message.error(result.error || "Failed to load templates");
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      message.error("Failed to load confirmation templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load confirmation status
  const loadConfirmations = () => {
    if (booking?.confirmations) {
      setConfirmations({
        whatsappSent: booking.confirmations.whatsappSent || false,
        emailSent: booking.confirmations.emailSent || false,
        reminderSent: booking.confirmations.reminderSent || false,
      });
    }
  };

  // Handle copy WhatsApp
  const handleCopyWhatsApp = async () => {
    if (!whatsappTemplate) return;

    const success = await copyToClipboard(whatsappTemplate);
    if (success) {
      setWhatsappCopied(true);
      message.success("WhatsApp template copied to clipboard!");
      setTimeout(() => setWhatsappCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  // Handle copy Email
  const handleCopyEmail = async () => {
    if (!editableEmailTemplate.body) return;

    const emailText = `Subject: ${editableEmailTemplate.subject}\n\n${editableEmailTemplate.body}`;
    const success = await copyToClipboard(emailText);
    if (success) {
      setEmailCopied(true);
      message.success("Email template copied to clipboard!");
      setTimeout(() => setEmailCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  // Handle confirmation checkbox change
  const handleConfirmationChange = async (type, checked) => {
    const updates = {
      ...confirmations,
      [type]: checked,
    };

    setUpdatingConfirmations(true);
    try {
      const result = await updateConfirmation(orderId, updates);
      if (result.success) {
        setConfirmations(updates);
        message.success("Confirmation status updated");
        if (onConfirmationUpdate) {
          onConfirmationUpdate(result.data);
        }
      } else {
        message.error(result.error || "Failed to update confirmation");
      }
    } catch (error) {
      console.error("Error updating confirmation:", error);
      message.error("Failed to update confirmation status");
    } finally {
      setUpdatingConfirmations(false);
    }
  };

  // Handle send email
  const handleSendEmail = async () => {
    if (!editableEmailTemplate.subject || !editableEmailTemplate.body) {
      message.warning("Please fill in both subject and body");
      return;
    }

    setSendingEmail(true);
    try {
      const response = await api.post(`/bookings/${orderId}/send-email`, {
        subject: editableEmailTemplate.subject,
        body: editableEmailTemplate.body,
        includePickupPdf: true,
      });

      if (response.success) {
        message.success("Booking confirmation email sent successfully!");
        // Update confirmation status
        setConfirmations((prev) => ({
          ...prev,
          emailSent: true,
        }));
        // Reload booking data if callback provided
        if (onConfirmationUpdate) {
          // Reload templates to get updated status
          await loadTemplates();
          await loadConfirmations();
        }
      } else {
        message.error(response.error || "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      message.error(error.response?.data?.error || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return dayjs(date).format("DD MMM YYYY");
  };

  const formatTimeWindow = (timeWindow) => {
    if (!timeWindow) return "N/A";
    if (typeof timeWindow === "string") return timeWindow;
    if (timeWindow.start && timeWindow.end) {
      return `${timeWindow.start} - ${timeWindow.end}`;
    }
    return "N/A";
  };

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          <Text>Booking Confirmation Templates</Text>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      {loadingTemplates ? (
        <Box sx={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <Box sx={{ marginTop: 2 }}>
            <Text>Loading templates...</Text>
          </Box>
        </Box>
      ) : (
        <>
          {/* Booking Summary */}
          {booking && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: "100%" }} size="small">
                <Box>
                  <Text type="secondary">Move Date: </Text>
                  <Text strong>{formatDate(booking.moveDate)}</Text>
                </Box>
                <Box>
                  <Text type="secondary">Time Window: </Text>
                  <Text strong>
                    <CalendarOutlined /> {formatTimeWindow(booking.pickupTimeWindow)}
                  </Text>
                </Box>
                {booking.assignedTeam && (
                  <Box>
                    <Text type="secondary">Team: </Text>
                    <Text strong>
                      <TeamOutlined /> {booking.assignedTeam.teamName || "TBD"}
                    </Text>
                  </Box>
                )}
                <Box>
                  <Text type="secondary">Status: </Text>
                  <Tag color={booking.status === "confirmed" ? "success" : "processing"}>
                    {booking.status || "scheduled"}
                  </Tag>
                </Box>
              </Space>
            </Card>
          )}

          {/* Confirmation Tracking */}
          <Card
            size="small"
            title="Confirmation Tracking"
            style={{ marginBottom: 16 }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Checkbox
                checked={confirmations.whatsappSent}
                onChange={(e) =>
                  handleConfirmationChange("whatsappSent", e.target.checked)
                }
                disabled={updatingConfirmations}
              >
                WhatsApp confirmation sent
              </Checkbox>
              <Checkbox
                checked={confirmations.emailSent}
                onChange={(e) =>
                  handleConfirmationChange("emailSent", e.target.checked)
                }
                disabled={updatingConfirmations}
              >
                Email confirmation sent
              </Checkbox>
              <Checkbox
                checked={confirmations.reminderSent}
                onChange={(e) =>
                  handleConfirmationChange("reminderSent", e.target.checked)
                }
                disabled={updatingConfirmations}
              >
                Reminder sent
              </Checkbox>
            </Space>
            {confirmations.whatsappSent && confirmations.emailSent && (
              <Alert
                message="Booking Confirmed"
                description="Both WhatsApp and Email confirmations have been sent. Booking status is now 'confirmed'."
                type="success"
                showIcon
                style={{ marginTop: 12 }}
              />
            )}
          </Card>

          {/* Templates Tabs */}
          <Tabs defaultActiveKey="whatsapp">
            <TabPane
              tab={
                <Space>
                  <MessageOutlined />
                  <Text>WhatsApp Template</Text>
                </Space>
              }
              key="whatsapp"
            >
              <Card>
                <Box sx={{ marginBottom: 2, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={whatsappCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyWhatsApp}
                    disabled={!whatsappTemplate}
                  >
                    {whatsappCopied ? "Copied!" : "Copy to Clipboard"}
                  </Button>
                </Box>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: "13px",
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  {whatsappTemplate || "No WhatsApp template available"}
                </Box>
              </Card>
            </TabPane>

            <TabPane
              tab={
                <Space>
                  <MailOutlined />
                  <Text>Email Template</Text>
                </Space>
              }
              key="email"
            >
              <Card>
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  {/* Subject Input */}
                  <Box>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Subject:
                    </Text>
                    <Input
                      value={editableEmailTemplate.subject}
                      onChange={(e) =>
                        setEditableEmailTemplate({
                          ...editableEmailTemplate,
                          subject: e.target.value,
                        })
                      }
                      placeholder="Email subject"
                      style={{ fontFamily: "monospace" }}
                    />
                  </Box>

                  {/* Body TextArea */}
                  <Box>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>
                      Body:
                    </Text>
                    <TextArea
                      value={editableEmailTemplate.body}
                      onChange={(e) =>
                        setEditableEmailTemplate({
                          ...editableEmailTemplate,
                          body: e.target.value,
                        })
                      }
                      placeholder="Email body"
                      rows={12}
                      style={{
                        fontFamily: "monospace",
                        fontSize: "13px",
                        whiteSpace: "pre-wrap",
                      }}
                    />
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Button
                      icon={emailCopied ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={handleCopyEmail}
                      disabled={!editableEmailTemplate.body}
                    >
                      {emailCopied ? "Copied!" : "Copy to Clipboard"}
                    </Button>
                    <Space>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={handleSendEmail}
                        loading={sendingEmail}
                        disabled={
                          !editableEmailTemplate.subject ||
                          !editableEmailTemplate.body ||
                          sendingEmail
                        }
                      >
                        {sendingEmail ? "Sending..." : "Send Email"}
                      </Button>
                    </Space>
                  </Box>
                </Space>
              </Card>
            </TabPane>
          </Tabs>

          {/* Close Button */}
          <Box sx={{ textAlign: "right", marginTop: 3 }}>
            <Button onClick={onClose}>Close</Button>
          </Box>
        </>
      )}
    </Modal>
  );
};

export default BookingConfirmationTemplatesModal;

